import { G } from '@mobily/ts-belt';
import { addDays, addYears, subYears } from 'date-fns';
import { required, Validator } from 'react-admin';

import { EMPLOYEE_MIN_LEGAL_WORK_AGE } from '@Common/constants/b2b/employees';
import type { B2bContractType } from '@Common/types/bo/b2b/contracts';
import { hasERContracts } from '@Common/types/bo/b2b/contracts';

import { toDateString, todayDateString, tryToLocalDate } from '@sh4res/bo-common/utils/date';
import { length } from '@sh4res/bo-common/utils/validators/length';
import { maxDate, minDate } from '@sh4res/bo-common/validators/date';

export const bicValidation = length([8, 11]);

export const taxNumberValidation = length(13);

export const dobValidation =
  (message?: string, minAge = EMPLOYEE_MIN_LEGAL_WORK_AGE) =>
  (value: string | Date) => {
    const maxDob = subYears(new Date(), minAge);
    const dateValue = tryToLocalDate(value);

    if (dateValue && dateValue > maxDob) {
      return (
        message ?? {
          message: 'resources.bo_common.b2b.employees.validation.dob',
          args: { minAge },
        }
      );
    }
  };

export const workEndingDate =
  (message?: string) =>
  (
    value: string | Date,
    { work_starting_date: workStartingDateValue }: { work_starting_date: string | Date },
  ) => {
    const workStartingDate = tryToLocalDate(workStartingDateValue);
    const workEndingDate = tryToLocalDate(value);

    if (workStartingDate && workEndingDate && workStartingDate > workEndingDate) {
      return message ?? 'resources.bo_common.b2b.employees.validation.work_ending_date';
    }
  };

export const workStartingDate =
  (minAge = EMPLOYEE_MIN_LEGAL_WORK_AGE) =>
  (value: string | Date, { date_of_birth: dateOfBirth }: { date_of_birth: string | Date }) => {
    const workStartingDate = tryToLocalDate(value);
    if (G.isNullable(workStartingDate)) {
      return;
    }

    const dobDate = tryToLocalDate(dateOfBirth);
    if (G.isNotNullable(dobDate)) {
      const workLegalAge = addYears(dobDate, minAge);

      if (workStartingDate < workLegalAge) {
        return {
          message: 'resources.bo_common.b2b.employees.validation.work_starting_date',
          args: { minAge },
        };
      }
    }

    if (workStartingDate > new Date()) {
      return 'resources.bo_common.b2b.employees.validation.work_starting_date_max';
    }
  };

export const getWorkEndingDateValidator = (
  status: string,
  leavingReason: string,
  hasChangedFromPastToCurrent = false,
) => {
  const validator: Validator[] = [workEndingDate()];

  if (leavingReason && status !== 'current') {
    validator.push(required());
  }

  if (status !== 'current') {
    validator.push(maxDate(todayDateString));
  }

  if (hasChangedFromPastToCurrent && status === 'current') {
    validator.push(minDate(() => toDateString(addDays(new Date(), 1))));
  }

  return validator;
};

export const ER_AFFILIATION_REQUIRES_TAIGA_ID_MESSAGE =
  'resources.bo_common.b2b.employees.validation.er_requires_taiga_policy_holder_id';

export const erAffiliationRequiresTaigaIdValidation =
  (
    contracts:
      | {
          id: number;
          contract_type: B2bContractType;
        }[]
      | undefined,
  ): Validator =>
  (
    _value,
    allValues: {
      contract_ids?: unknown;
      id_external_taiga?: unknown;
      taiga_policy_holder_id?: unknown;
    },
  ) => {
    let taigaId: number | null = null;
    if (typeof allValues.id_external_taiga === 'number') {
      taigaId = allValues.id_external_taiga;
    } else if (typeof allValues.taiga_policy_holder_id === 'number') {
      taigaId = allValues.taiga_policy_holder_id;
    }

    if (G.isNotNullable(taigaId) || !contracts) {
      return undefined;
    }

    const contractIds = Array.isArray(allValues.contract_ids)
      ? allValues.contract_ids.filter((id): id is number => typeof id === 'number')
      : [];
    const selectedContractTypes = contracts
      .filter((contract) => contractIds.includes(contract.id))
      .map((contract) => contract.contract_type);

    if (hasERContracts(selectedContractTypes)) {
      return ER_AFFILIATION_REQUIRES_TAIGA_ID_MESSAGE;
    }

    return undefined;
  };
